import { defineStoredString } from './storage';

/**
 * The signed-in session's bearer token.
 *
 * Kept through a wipe: out in the field this is the only proof of who is using
 * the device, and it cannot be fetched again from there. Signing out clears it
 * deliberately (`clearToken`).
 */
const sessionToken = defineStoredString({
    key: 'data_token',
    keepOnWipe: true
});

export function getToken(): string | null {
    return sessionToken.read();
}

export function setToken(token: string) {
    sessionToken.write(token);
}

export function clearToken() {
    sessionToken.clear();
}

/**
 * Hear about the token changing — including in ANOTHER TAB, which is the point:
 * signing out in one window has to end the session in the others, or they go on
 * showing an app whose credential is gone.
 *
 * The listener's `external` flag separates that from this tab's own writing.
 */
export const subscribeToken = sessionToken.subscribe;

export async function authFetch(
    input: string,
    init?: RequestInit
): Promise<Response> {
    return fetchAs(getToken(), input, init);
}

/**
 * Like `authFetch`, but with an explicitly chosen token instead of the
 * signed-in one. Used by the replication push to send a previous user's queued
 * changes under their own credential (see `db/authorJournal`).
 */
export async function fetchAs(
    token: string | null,
    input: string,
    init?: RequestInit
): Promise<Response> {
    const headers = new Headers(init?.headers);
    if (token) {
        headers.set('Authorization', `Bearer ${token}`);
    }
    return fetch(input, { ...init, headers });
}
